import { useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createSourcesApi, relativeAge } from '@bloo-team/api-client'
import type { SourceDevice, SourcesPage } from '@bloo-team/api-client'
import { client, getSession, hasRole, identityFor, isDemoSession } from '@bloo-team/core-shell'
import { applyDeletedOverlay, sourceStore, useSourceOverlay } from './source-store'

/**
 * Sources data — GET /{clusterId}/mgmt/core/device-management/.
 *
 * Demo mode returns fixtures in the SAME shape the live endpoint returns, so
 * the page has one code path and cannot drift into rendering something the API
 * could never produce. See packages/api-client/src/sources.ts for the captured
 * contract; note in particular that `total` is the server-side total across all
 * pages, not `devices.length`.
 *
 * Device delete (docs/fabric-overview-requirements.md §Device cleanup):
 * `DELETE mgmt/core/device-management/{scope}/{device}` is declared-only until
 * capture C1, so the live write is gated off (`WRITE_GATES`, `liveWriteOpen`).
 * Demo writes the localStorage overlay (`source-store.ts`), applied over the
 * fixtures by `useSources`.
 */

export const sourcesApi = createSourcesApi(client)

export { relativeAge }
export type { SourceDevice }

/** Seconds-since-epoch helper for building demo rows relative to now. */
const agoSecs = (s: number) => Date.now() / 1000 - s

/** Exported so tests can assert these validate against the LIVE-captured schema. */
export const DEMO_SOURCES: SourceDevice[] = [
  { device_address: '10.20.0.1', scope_id: 'gotham', status: 'active', description: null,
    extractor: ['fortinet-fortigate-syslog_kv'], extractor_id: ['4001'], stream: ['FORTIGATE', 'AUTHENTICATION'],
    create_tstamp: agoSecs(86_400 * 300), last_seen_tstamp: agoSecs(2), last_modified_tstamp: null },
  { device_address: '10.20.4.18', scope_id: 'gotham', status: 'active', description: null,
    extractor: ['ms-windows-winlogbeat'], extractor_id: ['4002'], stream: ['WIN-AUDIT'],
    create_tstamp: agoSecs(86_400 * 220), last_seen_tstamp: agoSecs(11), last_modified_tstamp: null },
  { device_address: '10.20.4.2', scope_id: 'gotham', status: 'active', description: null,
    extractor: ['ms-windows-nxlog'], extractor_id: ['4003'], stream: ['WIN-AUDIT', 'AUTHENTICATION'],
    // Decommissioned (pipeline-health.ts): still 'active' on the wire, unseen 40 days.
    create_tstamp: agoSecs(86_400 * 400), last_seen_tstamp: agoSecs(86_400 * 40), last_modified_tstamp: null },
  { device_address: '10.20.8.5', scope_id: 'gotham', status: 'inactive', description: null,
    extractor: ['broadcom-symantec-endpoint-syslog_kv'], extractor_id: ['4004'], stream: ['EP-PROCESS'],
    create_tstamp: agoSecs(86_400 * 150), last_seen_tstamp: agoSecs(1_440), last_modified_tstamp: null },
  // The common real shape: known device, no extractor configured (707 of 807).
  { device_address: '10.20.4.31', scope_id: 'gotham', status: 'inactive', description: null,
    extractor: [], extractor_id: [], stream: ['WIN-AUDIT'],
    create_tstamp: agoSecs(86_400 * 90), last_seen_tstamp: agoSecs(7_200), last_modified_tstamp: null },
  { device_address: '10.20.2.12', scope_id: 'gotham', status: 'active', description: null,
    extractor: ['nginx-access'], extractor_id: ['4005'], stream: ['AUTHENTICATION'],
    // Stale: 'active' on the wire, unseen 3 days.
    create_tstamp: agoSecs(86_400 * 80), last_seen_tstamp: agoSecs(86_400 * 3), last_modified_tstamp: null },
  { device_address: '10.20.0.9', scope_id: 'gotham', status: 'inactive', description: null,
    extractor: [], extractor_id: [], stream: ['FORTIGATE'],
    // Decommissioned and 'inactive': unseen 45 days.
    create_tstamp: agoSecs(86_400 * 500), last_seen_tstamp: agoSecs(86_400 * 45), last_modified_tstamp: null },
]

export interface UseSourcesArgs {
  clusterId: string
  scopeId: string
  pageNo: number
  pageSize: number
  /** `false` keeps the query idle until the caller actually needs the page (default `true`). */
  enabled?: boolean
}

export function useSources({ clusterId, scopeId, pageNo, pageSize, enabled = true }: UseSourcesArgs) {
  const isDemo = isDemoSession()
  const overlay = useSourceOverlay(clusterId, scopeId)
  const q = useQuery<SourcesPage>({
    queryKey: ['sources', clusterId, scopeId, pageNo, pageSize, isDemo ? 'demo' : 'live'],
    queryFn: async () => {
      if (isDemo) {
        const start = (pageNo - 1) * pageSize
        return { devices: DEMO_SOURCES.slice(start, start + pageSize), total: DEMO_SOURCES.length }
      }
      return sourcesApi.list({ clusterId, scopeId, pageNo, pageSize })
    },
    // Reads are idempotent GETs here, unlike most of this API's POST-shaped
    // reads, so a retry is safe.
    retry: 1,
    enabled,
  })
  // Demo deletes are applied OUTSIDE the query so the key (and so the cache
  // Home, Overview and Sources share) is unchanged. The overlay must filter
  // before the page is sliced, so the page is re-derived from the full fixture
  // set rather than from the cached slice; `q.data` only gates pending state.
  const data = useMemo<SourcesPage | undefined>(() => {
    if (!q.data || !isDemo) return q.data
    const all = applyDeletedOverlay(DEMO_SOURCES, overlay)
    if (all.length === DEMO_SOURCES.length) return q.data
    const start = (pageNo - 1) * pageSize
    return { devices: all.slice(start, start + pageSize), total: all.length }
  }, [q.data, isDemo, overlay, pageNo, pageSize])
  return { ...q, data }
}

/* ------------------------------------------------------------------ gates */

/** Capture gate per write (docs/fabric-overview-requirements.md §Capture ledger). */
export const WRITE_GATES = { deleteDevice: 'C1' } as const

export type WriteGate = keyof typeof WRITE_GATES

const GATE_ENDPOINT: Record<WriteGate, string> = {
  deleteDevice: 'DELETE mgmt/core/device-management/{scope}/{device}',
}

/**
 * Whether each gated live write is open. The single switch capture C1 flips:
 * until the delete's response is captured, live mode renders it disabled.
 */
export const LIVE_WRITE_OPEN: Record<WriteGate, boolean> = { deleteDevice: false }

export function liveWriteOpen(gate: WriteGate): boolean {
  return LIVE_WRITE_OPEN[gate]
}

/** The disabled button's title / the muted line beside the action. */
export function gateReason(gate: WriteGate): string {
  return `Write shape unconfirmed — capture ${WRITE_GATES[gate]} (${GATE_ENDPOINT[gate]}) pending (docs/fabric-overview-requirements.md §Capture ledger)`
}

/* ----------------------------------------------------------------- writes */

/**
 * Who may delete a device. Legacy gated device management on Security
 * Engineer, like the extraction screen (`canWriteExtractors`). A UX
 * affordance — authorization is the server's (permissions.ts).
 */
export function canDeleteSources(clusterId: string, scopeId: string): boolean {
  const session = getSession()
  if (!session) return false
  return hasRole(identityFor(session, clusterId, scopeId), ['Security Engineer'])
}

export interface DeleteResult {
  deleted: string[]
  failed: Array<{ address: string; message: string }>
}

/**
 * Delete devices by address. Demo marks them deleted in the overlay. Live is
 * refused with `gateReason` while C1 is closed; once open it deletes one
 * address at a time, in order, and a failure does NOT stop the rest — the
 * result names what landed and what did not, so a bulk cleanup can report a
 * partial outcome instead of hiding it.
 */
export function useDeleteSources({ clusterId, scopeId }: { clusterId: string; scopeId: string }) {
  const qc = useQueryClient()
  return useMutation<DeleteResult, Error, string[]>({
    mutationFn: async (addresses) => {
      if (isDemoSession()) {
        sourceStore(clusterId, scopeId).markDeleted(addresses)
        return { deleted: [...addresses], failed: [] }
      }
      if (!liveWriteOpen('deleteDevice')) throw new Error(gateReason('deleteDevice'))
      const result: DeleteResult = { deleted: [], failed: [] }
      for (const address of addresses) {
        try {
          await sourcesApi.remove({ clusterId, scopeId, deviceAddress: address })
          result.deleted.push(address)
        } catch (e) {
          result.failed.push({ address, message: e instanceof Error ? e.message : String(e) })
        }
      }
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['sources', clusterId, scopeId] }),
        qc.invalidateQueries({ queryKey: ['stream-sources', clusterId, scopeId] }),
      ])
      return result
    },
  })
}

/* ------------------------------------------------------------------ display */

/** `['a','b','c']` -> `'a +2'`. Empty stays empty so the caller can say "none". */
export function summariseList(xs: string[]): string {
  const [first, ...rest] = xs
  if (!first) return ''
  return rest.length ? `${first} +${rest.length}` : first
}

/** ISO date (no time) for the "first seen" column; empty when absent. */
export function isoDate(t: number | null | undefined): string {
  if (typeof t !== 'number' || !Number.isFinite(t)) return ''
  return new Date(t * 1000).toISOString().slice(0, 10)
}

/**
 * Does this device run the named extractor? Exact, case-insensitive match on
 * the extractor NAMES the device carries — the `?extractor=` pivot from
 * Datafabric · Extractors lands here.
 */
export function matchesExtractor(d: SourceDevice, extractor: string | undefined): boolean {
  const want = extractor?.trim().toLowerCase()
  if (!want) return true
  return d.extractor.some((x) => x.trim().toLowerCase() === want)
}
